import { useEffect, useRef, useState } from "react";
import { Scissors, Play } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

function fmt(t) {
  if (t == null || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60), s = Math.floor(t % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// Lightweight trim editor: pick start/end; preview plays only that segment.
export default function TrimDialog({ open, onClose, item, onSave }) {
  const ref = useRef(null);
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && item) {
      setStart(typeof item.trim_start === "number" ? item.trim_start : 0);
      setEnd(typeof item.trim_end === "number" ? item.trim_end : 0);
    }
  }, [open, item]);

  const onMeta = () => {
    const d = ref.current?.duration || 0;
    setDuration(d);
    if (!end || end > d) setEnd(typeof item?.trim_end === "number" ? item.trim_end : d);
  };

  const preview = () => {
    const v = ref.current;
    if (!v) return;
    v.currentTime = start;
    v.play();
    const stop = () => { if (v.currentTime >= end) { v.pause(); v.removeEventListener("timeupdate", stop); } };
    v.addEventListener("timeupdate", stop);
  };

  const save = async () => {
    setSaving(true);
    const ts = Math.max(0, Math.min(start, end));
    const te = Math.max(start, end) || duration;
    await onSave({ trim_start: ts, trim_end: te });
    setSaving(false);
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="trim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Scissors className="h-5 w-5 text-[#FF5A79]" /> Trim clip
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <video ref={ref} src={item.url} controls playsInline onLoadedMetadata={onMeta}
            className="w-full max-h-64 rounded-2xl bg-black object-contain" data-testid="trim-video" />
          <div className="text-sm text-slate-500 text-center">
            Keeping <b className="text-slate-800">{fmt(start)}</b> → <b className="text-slate-800">{fmt(end)}</b> of {fmt(duration)}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400">Start — {fmt(start)}</label>
            <input type="range" min={0} max={duration || 0} step={0.1} value={start} data-testid="trim-start-range"
              onChange={(e) => setStart(Math.min(parseFloat(e.target.value), end))}
              className="w-full accent-[#FF5A79]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-400">End — {fmt(end)}</label>
            <input type="range" min={0} max={duration || 0} step={0.1} value={end} data-testid="trim-end-range"
              onChange={(e) => setEnd(Math.max(parseFloat(e.target.value), start))}
              className="w-full accent-[#2AB7CA]" />
          </div>
          <Button variant="outline" onClick={preview} data-testid="trim-preview-btn" className="rounded-full w-full">
            <Play className="h-4 w-4 mr-1.5" /> Preview selection
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="trim-save-btn" className="rounded-full bg-[#FF5A79] hover:bg-[#e04865] font-bold">
            {saving ? "Saving…" : "Save trim"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
